import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MarkerService } from '../add-marker-dialog/marker.service';
import type { DeleteMarkerDialogData } from './delete-marker-dialog-data';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatError, MatFormField, MatLabel } from '@angular/material/input';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { TranslocoDirective } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ValidatorErrorPipe } from '../../pipes/validator-error';

@Component({
  selector: 'app-delete-marker-dialog',
  imports: [
    FormsModule,
    MatButton,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    TranslocoDirective,
    ReactiveFormsModule,
    MatError,
    ValidatorErrorPipe,
  ],
  providers: [MarkerService],
  templateUrl: './delete-marker-dialog.component.html',
  styleUrl: './delete-marker-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeleteMarkerDialogComponent {
  private readonly dialogRef = inject<MatDialogRef<DeleteMarkerDialogComponent, boolean>>(MatDialogRef);
  protected readonly data = inject<DeleteMarkerDialogData>(MAT_DIALOG_DATA);
  private readonly markerService = inject(MarkerService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly participantControl = new FormControl<string[]>(
    this.data.participantOptions
      .filter(participant => this.data.selectedMindDeviceIds.includes(participant.mindDeviceId))
      .map(participant => participant.mindDeviceId),
    {
      validators: [Validators.required],
      nonNullable: true,
    },
  );

  protected readonly form = new FormGroup({
    participant: this.participantControl,
  });

  protected save(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    this.markerService
      .deleteMarker(this.data.recordingId, this.data.marker.groupId, this.participantControl.value)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.dialogRef.close(true));
  }

  protected cancel(): void {
    this.dialogRef.close();
  }
}
